import { Box, Chip, LinearProgress, Stack, Typography } from "@mui/material"
import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Sstv2MigrationCallout } from "./Sstv2MigrationCallout"
import { Sstv2StreamLegend } from "./Sstv2StreamLegend"
import { Sstv2TermChip, Sstv2TermLabel } from "./Sstv2TermLabel"
import {
  Sstv2MigrationRow,
  StreamProgress,
  computeStreamProgress,
  formatStreamProgress,
  processingStateChipColor,
} from "./sstv2MigrationTypes"
import {
  Sstv2StreamKey,
  describeProcessingState,
  describeStream,
  describeStreamProvenance,
} from "./sstv2Vocabulary"

const labelSx = {
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.1em",
  textTransform: "uppercase" as const,
  color: "text.secondary",
}

/**
 * One reservation-stream progress bar (ACTIVE or HISTORICAL). The bar fills to
 * done/received (flipped + adopted + failed); failures are called out with a red
 * badge and a red overlay segment. No yellow/amber fills (repo convention).
 *
 * The title is a glossed term rather than plain text, because "active" vs
 * "historical" is the distinction D37 changed and the one an operator is most likely
 * to hold a stale mental model of. The legend below the bar names every counter.
 */
const StreamBar: FC<{ stream: Sstv2StreamKey; progress: StreamProgress }> = ({
  stream,
  progress,
}) => {
  const { t } = useTranslation()
  const term = describeStream(stream)
  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 0.5 }}
      >
        <Sstv2TermLabel term={term} color="text.primary">
          <Box component="span" sx={{ fontWeight: 600 }}>
            {t(term.labelKey)}
          </Box>
        </Sstv2TermLabel>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {progress.empty ? "—" : formatStreamProgress(progress)}
          </Typography>
          {/* PROVENANCE, beside the numbers it qualifies — the one thing the bar
              cannot express. A full bar and a certified stream are different facts,
              and `ace1c2c7…` is the proof: its history counters balanced perfectly
              because ingestion credited them at write-down while nothing ever walked
              the partition. Styled INFO when proven and neutral when not, never error:
              "not yet certified" is the ordinary state of a stream still being worked
              and of every attempt older than the stamp. The callout below is what
              escalates it when it sits next to COMPLETE. */}
          <Sstv2TermChip
            term={describeStreamProvenance(
              progress.proven ? "proven" : "unproven",
            )}
            color={progress.proven ? "info" : "default"}
          />
          {progress.hasFailures && (
            <Chip
              size="small"
              color="error"
              label={t("migrationFailedBadge")}
              sx={{ height: 20, fontSize: "0.68rem" }}
            />
          )}
        </Stack>
      </Stack>
      {/* Base bar shows overall done%; a thin red overlay marks the failed share. */}
      <Box sx={{ position: "relative" }}>
        <LinearProgress
          variant="determinate"
          value={progress.pct}
          color={progress.hasFailures ? "warning" : "primary"}
          sx={{
            height: 8,
            borderRadius: 1,
            // Neutralise the default amber "warning" track/bar fill — we only
            // reuse the slot; the actual fill colours are overridden to
            // primary/error so there is no yellow (repo convention).
            bgcolor: "rgba(35, 66, 133, 0.08)",
            "& .MuiLinearProgress-bar": { bgcolor: "primary.main" },
          }}
        />
        {progress.hasFailures && (
          <LinearProgress
            variant="determinate"
            value={progress.failPct}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 8,
              borderRadius: 1,
              bgcolor: "transparent",
              "& .MuiLinearProgress-bar": { bgcolor: "error.main" },
            }}
          />
        )}
      </Box>
      <Box sx={{ mt: 0.75 }}>
        <Sstv2StreamLegend progress={progress} />
      </Box>
    </Box>
  )
}

/**
 * DEV-193 — per-job pipeline progress: OUR processingState, two reservation
 * progress bars, and the advisory banners. Rendered in the migration detail
 * drawer. Extracted to keep the drawer under the ~400-line component limit.
 *
 * ⚠ REVISED 2026-07-31 — D37 supersedes D5. The two bars now measure the SAME thing
 * by the same mechanism: the durable flip walks that stream's mapping partition and
 * rewrites each row in place. What still differs is WHEN each finishes:
 *
 *  - **ACTIVE** is walked first and is O(occupied doors) — tens of rows, usually one
 *    page. It gates locker liveness: an unflipped active reservation is an occupied
 *    door the dashboard cannot open.
 *  - **HISTORICAL** is walked second, behind its own arrival gate, and is O(every
 *    reservation the locker ever held) — thousands of rows, a background drain that
 *    can still be running long after cutover.
 *
 * So a **full active bar beside a partial history bar is a normal mid-migration
 * state**, not a fault, and it is the state this panel most needs to be legible in.
 *
 * This comment previously said history "never gets flipped at all (D5)" and that its
 * bar filled at INGESTION. Both were reversed by `d9047759b`: the ingestion-time
 * `historyProcessed` auto-credit was removed in the same commit, because leaving it
 * would have fired `COMPLETE` before the history walk ran.
 */
export const Sstv2MigrationJobProgress: FC<{ row: Sstv2MigrationRow }> = ({
  row,
}) => {
  const { t } = useTranslation()

  const active = computeStreamProgress(
    row.activeReceived,
    row.activeProcessed,
    row.activeFailed,
    row.activeAdopted,
    row.activeFoldedCount,
  )
  const history = computeStreamProgress(
    row.historyReceived,
    row.historyProcessed,
    row.historyFailed,
    row.historyAdopted,
    row.historyFoldedCount,
  )

  const processingTerm = useMemo(
    () => describeProcessingState(row.processingState),
    [row.processingState],
  )

  /**
   * Active finished, history not — the normal consequence of D37's sequential walk,
   * and the state most likely to be mistaken for a stall. Only meaningful once the
   * partner has actually sent history, hence the `!history.empty` guard.
   */
  const historyStillDraining =
    !active.empty && active.pct >= 100 && !history.empty && history.pct < 100

  return (
    <Box>
      <Typography sx={{ ...labelSx, mb: 1 }}>
        {t("migrationJobProgress")}
      </Typography>

      <Stack spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Typography variant="body2" color="text.secondary">
            {t("migrationProcessingState")}
          </Typography>
          {/* The chip carries its own gloss, so "DRAINING_HISTORY" explains itself
              where it is rendered. An UNMODELLED state renders as "Unknown (RAW)" in
              neutral colour rather than falling through to a plausible default —
              see the extension point in `./sstv2Vocabulary`. */}
          <Sstv2TermChip
            term={processingTerm}
            color={processingStateChipColor(row.processingState)}
          />
        </Stack>

        {/* "You are here, and this is what happens next." The phase ladder and
            processingState are both jargon, and the partner's phase and our pipeline
            state routinely disagree — most confusingly partner COMPLETED (they
            finished SENDING) against our COMPLETE (we finished WRITING). */}
        <Typography variant="caption" color="text.secondary">
          {t(processingTerm.glossKey)}
        </Typography>

        {/* SEVERE (2026-07-28): the active stream finished having flipped and
            adopted nothing, so every occupied door on the V2 locker is unlinked
            and unusable for operators. `processingState: COMPLETE` says nothing
            about this — migration 76f9def2… reported COMPLETE in exactly this
            state. Post-in-place the most likely cause is a flip that never ran;
            the attempt-state panel below distinguishes that from a flip that ran
            and failed per-row. */}
        {row.activeMaterializationEmpty && (
          <Sstv2MigrationCallout
            severity="severe"
            title={t("migrationActiveMaterializationEmpty")}
            hint={t("migrationActiveMaterializationEmptyHint")}
          />
        )}

        {/* Advisory: the cutover ran ahead of the active stream. */}
        {row.activeDiscrepancy && (
          <Sstv2MigrationCallout
            severity="advisory"
            title={t("migrationActiveDiscrepancy")}
            hint={t("migrationActiveDiscrepancyHint")}
          />
        )}

        {/* Advisory: finished, but not everything made it. Suppressed when the
            severe banner already says something stronger. */}
        {row.completedWithFailures && !row.activeMaterializationEmpty && (
          <Sstv2MigrationCallout
            severity="advisory"
            title={t("migrationCompletedWithFailures")}
            hint={t("migrationCompletedWithFailuresHint")}
          />
        )}

        <StreamBar stream="active" progress={active} />
        <StreamBar stream="history" progress={history} />

        {/* The single most confusing legitimate state on this panel, said out loud
            so nobody reports it as a stuck migration: active done, history not. */}
        {historyStillDraining && (
          <Typography variant="caption" color="text.secondary">
            {t("migrationHistoryStillDraining")}
          </Typography>
        )}

        {row.operationalEventReceived && (
          <Typography variant="caption" color="text.secondary">
            {t("migrationOperationalReceived")}
          </Typography>
        )}
      </Stack>
    </Box>
  )
}

export default Sstv2MigrationJobProgress
